/**
 * AnnouncementsCard — shared aspect. Small click-through card with
 * a list of recent posts. Header has an eyebrow + optional unread
 * count badge. Body either shows an empty-state line or the top N
 * announcements (each: left amber accent border, date label, title).
 * Footer is a "view all" link.
 *
 * Consumer pre-formats each item's dateLabel — keeps the aspect
 * stateless and locale-agnostic.
 */
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import { RADII, STROKES } from "../../lib/brand";
import Svg, { Path } from "react-native-svg";

import type { PaneTheme } from "../brand/paneThemes";

export type AnnouncementsCardItem = {
  id: string;
  /** Pre-formatted date label like "Today · 3:42 PM", "Yesterday", "May 3". */
  dateLabel: string;
  title: string;
  viewed: boolean;
};

export type AnnouncementsCardProps = {
  theme: PaneTheme;
  items: AnnouncementsCardItem[];
  unreadCount?: number;
  /** Max items rendered before the "view all" link. Default 2. */
  maxVisible?: number;
  /** Eyebrow/header label. Default "ANNOUNCEMENTS". */
  title?: string;
  /** Empty-state body copy. */
  emptyText?: string;
  /** Footer link label. Default "VIEW ALL". */
  viewAllLabel?: string;
  /** Whole card click + footer link both fire this. */
  onPressViewAll: () => void;
  /** Individual row click. Stops propagation to the card click. */
  onPressItem?: (id: string) => void;
  accessibilityLabel?: string;
};

export function AnnouncementsCard(props: AnnouncementsCardProps) {
  const {
    theme,
    items,
    unreadCount,
    maxVisible = 2,
    title = "ANNOUNCEMENTS",
    emptyText = "No announcements for this program yet. Anything the host posts will show up here.",
    viewAllLabel = "VIEW ALL",
    onPressViewAll,
    onPressItem,
    accessibilityLabel,
  } = props;
  const isWeb = Platform.OS === "web";
  const top = items.slice(0, maxVisible);

  return (
    <Pressable
      onPress={onPressViewAll}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? viewAllLabel}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          flex: 1,
          backgroundColor: theme.surfaceRaisedBg,
          borderRadius: RADII.lg,
          borderWidth: 0.5,
          borderColor:
            hovered && isWeb
              ? "rgba(28,43,26,0.32)"
              : "rgba(28,43,26,0.18)",
          padding: 18,
          minHeight: 168,
        },
        isWeb ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 10,
            letterSpacing: 1.4,
            color: theme.accent,
          }}
        >
          {title}
        </Text>
        {unreadCount && unreadCount > 0 ? (
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10,
              color: theme.accent,
              letterSpacing: 0.6,
            }}
          >
            {unreadCount + " unread"}
          </Text>
        ) : null}
      </View>

      {top.length === 0 ? (
        <View style={{ paddingVertical: 18, alignItems: "flex-start" }}>
          <Text
            style={{
              fontFamily: theme.bodyFont,
              fontSize: 12.5,
              color: theme.textOnSurfaceFaint,
              lineHeight: 17,
            }}
          >
            {emptyText}
          </Text>
        </View>
      ) : (
        <View style={{ marginTop: 10, gap: 10 }}>
          {top.map((a) => (
            <Pressable
              key={a.id}
              onPress={(e) => {
                if (
                  e &&
                  typeof (e as { stopPropagation?: () => void }).stopPropagation === "function"
                ) {
                  (e as { stopPropagation: () => void }).stopPropagation();
                }
                if (onPressItem) onPressItem(a.id);
              }}
              style={({ hovered }: { hovered?: boolean }) => [
                {
                  borderLeftWidth: STROKES.strong,
                  borderLeftColor: a.viewed
                    ? "rgba(28,43,26,0.18)"
                    : theme.accent,
                  paddingLeft: 10,
                  paddingVertical: 2,
                  backgroundColor:
                    hovered && isWeb
                      ? "rgba(181,135,58,0.04)"
                      : "transparent",
                },
                isWeb ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
              ]}
            >
              <Text
                numberOfLines={1}
                style={{
                  fontFamily: theme.bodyFont,
                  fontSize: 9,
                  color: theme.textOnSurfaceFaint,
                  marginBottom: 2,
                  letterSpacing: 0.4,
                }}
              >
                {a.dateLabel}
              </Text>
              <Text
                numberOfLines={2}
                style={{
                  fontFamily: a.viewed ? theme.bodyMediumFont : theme.bodyBoldFont,
                  fontSize: 13,
                  color: theme.textOnSurface,
                  lineHeight: 17,
                }}
              >
                {a.title}
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      <View
        style={{
          marginTop: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 11,
            letterSpacing: 1.2,
            color: theme.accent,
          }}
        >
          {viewAllLabel}
        </Text>
        <Arrow theme={theme} />
      </View>
    </Pressable>
  );
}

function Arrow({ theme }: { theme: PaneTheme }) {
  return (
    <Svg width={10} height={10} viewBox="0 0 12 12">
      <Path
        d="M2 6 H10 M7 3 L10 6 L7 9"
        stroke={theme.accent}
        strokeWidth={1.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}
